// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { 
  format, 
  startOfDay, 
  startOfWeek, 
  startOfMonth, 
  differenceInDays, 
  differenceInMonths,
  addDays, 
  addWeeks,
  addMonths,
  eachDayOfInterval, 
  eachWeekOfInterval,
  eachMonthOfInterval,
  isSameDay, 
  isSameWeek,
  isSameMonth,
  parseISO
} from 'date-fns';
import "./EventsOverTime.scss";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function EventsOverTime({ logs, timeframe }) {
  // Generate data for chart with adaptive time buckets based on date range
  const chartData = useMemo(() => {
    if (!logs || logs.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    // Sort logs by timestamp
    const sortedLogs = [...logs].sort((a, b) => 
      a.timestamp.toDate().getTime() - b.timestamp.toDate().getTime()
    );
    
    // Determine time granularity based on date range
    const startDate = timeframe.startDate;
    const endDate = timeframe.endDate;
    const daysDiff = differenceInDays(endDate, startDate);
    const monthsDiff = differenceInMonths(endDate, startDate);
    
    let timeUnit;
    let formatPattern;
    let intervalFn;
    let sameDateFn;
    let addTimeFn;
    let startOfTimeFn;

    // Choose time granularity based on date range
    if (daysDiff <= 31) {
      // For up to a month, use daily buckets
      timeUnit = 'day';
      formatPattern = 'MMM dd';
      intervalFn = eachDayOfInterval;
      sameDateFn = isSameDay;
      addTimeFn = addDays;
      startOfTimeFn = startOfDay;
    } else if (daysDiff <= 90) {
      // For up to three months, use weekly buckets
      timeUnit = 'week';
      formatPattern = 'MMM dd';
      intervalFn = eachWeekOfInterval;
      sameDateFn = isSameWeek;
      addTimeFn = addWeeks;
      startOfTimeFn = startOfWeek;
    } else {
      // For longer periods, use monthly buckets
      timeUnit = 'month';
      formatPattern = 'MMM yyyy';
      intervalFn = eachMonthOfInterval;
      sameDateFn = isSameMonth;
      addTimeFn = addMonths;
      startOfTimeFn = startOfMonth;
    }
    
    // Generate all time intervals in the range
    const intervalRange = intervalFn({
      start: startOfTimeFn(startDate),
      end: endDate
    });

    // Create labels for each interval
    const labels = intervalRange.map(date => format(date, formatPattern));
    
    // Group events by source
    const sourceColors = {
      dashboard: {
        borderColor: 'rgba(63, 185, 80, 1)',
        backgroundColor: 'rgba(63, 185, 80, 0.1)'
      },
      stamp: {
        borderColor: 'rgba(187, 128, 9, 1)',
        backgroundColor: 'rgba(187, 128, 9, 0.1)'
      },
      assistant: {
        borderColor: 'rgba(219, 97, 162, 1)',
        backgroundColor: 'rgba(219, 97, 162, 0.1)'
      }
    };

    // Group logs by source then by interval
    const logsBySource = {};

    // Initialize with all sources and intervals
    const sources = [...new Set(logs.map(log => log.source))];
    
    sources.forEach(source => {
      logsBySource[source] = {};
      intervalRange.forEach(date => {
        const intervalKey = format(date, 'yyyy-MM-dd');
        logsBySource[source][intervalKey] = 0;
      });
    });
    
    // Count logs for each source and interval
    logs.forEach(log => {
      const date = log.timestamp.toDate();
      const source = log.source;
      
      // Find matching interval
      const matchingInterval = intervalRange.find(interval => 
        sameDateFn(date, interval, { weekStartsOn: 1 })
      );
      
      if (matchingInterval && logsBySource[source]) {
        const intervalKey = format(matchingInterval, 'yyyy-MM-dd');
        logsBySource[source][intervalKey] = (logsBySource[source][intervalKey] || 0) + 1;
      }
    });
    
    // Create datasets for each source
    const datasets = sources.map(source => {
      const data = intervalRange.map(date => {
        const intervalKey = format(date, 'yyyy-MM-dd');
        return logsBySource[source][intervalKey] || 0;
      });
      
      return {
        label: source,
        data,
        borderColor: sourceColors[source]?.borderColor || 'rgba(66, 153, 225, 1)',
        backgroundColor: sourceColors[source]?.backgroundColor || 'rgba(66, 153, 225, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: sourceColors[source]?.borderColor || 'rgba(66, 153, 225, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: sourceColors[source]?.borderColor || 'rgba(66, 153, 225, 1)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
      };
    });
    
    // Add total events dataset
    const totalData = intervalRange.map(date => {
      const intervalKey = format(date, 'yyyy-MM-dd');
      return sources.reduce((sum, source) => sum + (logsBySource[source][intervalKey] || 0), 0);
    });
    
    datasets.unshift({
      label: 'Total',
      data: totalData,
      borderColor: 'rgba(66, 144, 245, 1)',
      backgroundColor: 'rgba(66, 144, 245, 0.1)',
      fill: true,
      tension: 0.4,
      borderWidth: 2,
      pointRadius: 4,
      pointBackgroundColor: 'rgba(66, 144, 245, 1)',
      pointBorderColor: '#fff',
      pointBorderWidth: 1,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: 'rgba(66, 144, 245, 1)',
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
    });
    
    return {
      labels,
      datasets,
      timeUnit
    };
  }, [logs, timeframe]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#24292e',
        bodyColor: '#24292e',
        borderColor: '#eaecef',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          title: function(context) {
            const title = context[0].label;
            const unit = chartData.timeUnit === 'day' ? 'day' : 
                         chartData.timeUnit === 'week' ? 'week' : 'month';
            return `${title} (${unit})`;
          }
        },
        bodyFont: {
          size: 13
        },
        titleFont: {
          size: 14,
          weight: 'bold'
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          },
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#eaecef',
          drawBorder: false
        },
        ticks: {
          padding: 10,
          stepSize: 1,
          precision: 0,
          font: {
            size: 12
         }
        }
      }
    }
  };

  if (!logs || logs.length === 0) {
    return (
      <div className="events-over-time-chart empty-state">
        <h3>Events Over Time</h3>
        <p>No events data available</p>
      </div>
    );
  }

  return (
    <div className="events-over-time-chart">
      <h3>Events Over Time</h3>
      <div className="time-unit-indicator">
        {chartData.timeUnit === 'day' ? 'Daily view' : 
         chartData.timeUnit === 'week' ? 'Weekly view' : 'Monthly view'}
      </div>
      <div className="chart-container">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
} 